import {
  ApplicationInsights,
  ITelemetryItem,
  SeverityLevel,
} from "@microsoft/applicationinsights-web";

import { isBrowser } from "../utils";

// Parity with the retired Sentry config: noisy, non-actionable browser errors.
const IGNORED_ERROR_PATTERNS = [
  "Fetch is aborted",
  "NS_ERROR_FILE_NO_DEVICE_SPACE",
  "ResizeObserver loop limit exceeded",
  "ResizeObserver loop completed with undelivered notifications.",
];

// Error-storm guard: the App Insights resource shares a daily-capped workspace
// with unbl-services.
const MAX_EXCEPTIONS_PER_SESSION = 50;

// Automated clients (Azure's availability monitor, crawlers) fail the
// referrer-restricted mapbox fetches on every run, drowning real user errors in
// the Failures blade. Matches only the agents actually seen doing this.
const isSyntheticClient = (): boolean => {
  if (typeof navigator === "undefined") {
    return false;
  }
  // Set by every automation driver (Selenium/Puppeteer/Playwright headless).
  if ((navigator as { webdriver?: boolean }).webdriver) {
    return true;
  }
  const ua = (navigator.userAgent || "").toLowerCase();
  return ua.includes("headlesschrome") || ua.includes("crawler");
};

let appInsights: ApplicationInsights = null;
let exceptionCount = 0;
const reportedKeys = new Set<string>();

export interface ITelemetryOptions {
  connectionString?: string;
  roleName: string;
  environment?: string;
}

/**
 * Errors-only Application Insights bootstrap. No-op during SSR, when no
 * connection string is configured (e.g. local dev), or when already
 * initialized.
 */
export const initTelemetry = ({
  connectionString,
  roleName,
  environment,
}: ITelemetryOptions) => {
  if (!isBrowser() || !connectionString || appInsights || isSyntheticClient()) {
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      disableAjaxTracking: true,
      disableFetchTracking: true,
      disableCorrelationHeaders: true,
      enableAutoRouteTracking: false,
      autoTrackPageVisitTime: false,
      disableExceptionTracking: false,
      // off by default in the SDK — must opt in
      enableUnhandledPromiseRejectionTracking: true,
      samplingPercentage: 100,
      // no ai_user/ai_session cookies — keeps error tracking outside the
      // analytics-consent regime
      disableCookiesUsage: true,
    },
  });
  appInsights.loadAppInsights();

  appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
    // Only exception telemetry ever leaves the browser (no page views,
    // dependencies, metrics etc.).
    if (item.baseType !== "ExceptionData") {
      return false;
    }
    if (exceptionCount >= MAX_EXCEPTIONS_PER_SESSION) {
      return false;
    }
    const message = item.baseData?.exceptions?.[0]?.message || "";
    if (IGNORED_ERROR_PATTERNS.some((pattern) => message.includes(pattern))) {
      return false;
    }
    exceptionCount += 1;

    item.tags = item.tags || ({} as typeof item.tags);
    item.tags["ai.cloud.role"] = roleName;
    if (environment) {
      item.data = item.data || {};
      item.data.environment = environment;
    }
  });
};

/**
 * Query strings carry user-entered text — the admin user search and the map's
 * place search both end up in one — so only the path is ever reported. It is
 * what a failure gets triaged on anyway.
 */
const stripQuery = (url: any): string => String(url).split(/[?#]/)[0];

// Enough keys to recognise a payload, not so many that a wide record fills the
// message. The workspace daily cap is shared with unbl-services.
const MAX_DESCRIBED_KEYS = 12;

/**
 * Last resort for a value with no recognisable error shape — `String()` on one
 * gives "[object Object]", which is unactionable in the portal. Reachable via
 * `BaseAPIService`, which throws the deserialized response body (not the
 * response) when the payload has a `data` key.
 *
 * Reports the shape only. The value here is a response body, which for the
 * admin resources means user records, so its contents must not be serialised —
 * knowing which keys arrived is what identifies the payload anyway.
 */
const describeUnknown = (value: any): string => {
  if (!value || typeof value !== "object") {
    return String(value);
  }

  try {
    if (Array.isArray(value)) {
      return `[array of ${value.length}]`;
    }

    const keys = Object.keys(value);
    if (!keys.length) {
      return "[object with no keys]";
    }

    const shown = keys.slice(0, MAX_DESCRIBED_KEYS).join(", ");
    const rest = keys.length - MAX_DESCRIBED_KEYS;

    return `[object with keys: ${shown}${rest > 0 ? ` and ${rest} more` : ""}]`;
  } catch {
    // telemetry must never throw back into the catch block that called it
    return String(value);
  }
};

/**
 * The API services reject with the raw axios response rather than an Error, so
 * anything reported straight from a catch would arrive with no message and no
 * stack. Rebuild something readable from whatever we were handed.
 */
const toError = (value: any): Error => {
  if (value instanceof Error) {
    return value;
  }

  if (value && typeof value === "object") {
    const status = value.status ?? value.statusCode;
    const url = value.config?.url ?? value.url;
    const detail =
      value.data?.errors?.[0]?.detail ??
      value.errors?.[0]?.detail ??
      value.statusText ??
      value.message;

    if (status || url || detail) {
      return new Error(
        [status && `HTTP ${status}`, detail, url && `(${stripQuery(url)})`]
          .filter(Boolean)
          .join(" ")
      );
    }
  }

  return new Error(describeUnknown(value));
};

/**
 * Manual exception report for errors the app handles itself — without these,
 * anything caught in a `catch` is invisible to monitoring.
 *
 * Safe no-op when telemetry is not initialized. Pass `dedupeKey` for errors
 * that can repeat on a timer or a retry, so one broken thing reports once
 * instead of exhausting the per-session budget.
 */
export const trackException = (
  error: any,
  properties?: Record<string, any>,
  options: { dedupeKey?: string } = {}
) => {
  if (!appInsights) {
    return;
  }

  const { dedupeKey } = options;
  if (dedupeKey) {
    if (reportedKeys.has(dedupeKey)) {
      return;
    }
    reportedKeys.add(dedupeKey);
  }

  appInsights.trackException(
    { exception: toError(error), severityLevel: SeverityLevel.Error },
    properties
  );
};

/**
 * SWR global `onError`. SWR turns every fetch rejection into an `error` state
 * rather than throwing, so without this no failed API load is visible to
 * monitoring. Deduped per path+status: SWR retries a failing key on a backoff,
 * and two searches failing the same way are the same failure.
 */
export const trackFetchError = (error: any, key: string) => {
  const status = error?.status ?? error?.statusCode ?? error?.response?.status;

  // 401 is the ordinary expiry path — the interceptor refreshes and replays,
  // and a refresh that actually fails reports itself.
  if (status === 401) {
    return;
  }

  const path = stripQuery(key);

  trackException(
    error,
    { source: "swr", swrPath: path, status: status ? String(status) : "none" },
    { dedupeKey: `swr:${path}:${status}` }
  );
};

/**
 * Adds `trackFetchError` to a hook's own SWR options.
 *
 * The shared fetch hooks can't rely on the apps' `<SWRConfig>` for this:
 * earth-map resolves its own copy of swr, so a provider rendered there is backed
 * by a different React context than the one earth-shared's hooks read, and its
 * `onError` never reaches them. Passing the handler per-hook is independent of
 * which copy a hook is bound to.
 *
 * `reportedUrl` replaces the key SWR hands the handler. `useSWRInfinite` keys on
 * a hashed array, so its key arrives as `arg@"many"@"<url>"` rather than a url —
 * the hooks pass their own base url instead, which keeps `swrPath` the same
 * shape across all three of them.
 *
 * `swrOptions` is spread last so a call site can still supply its own `onError`.
 */
export const withFetchErrorTracking = (
  swrOptions?: {
    [key: string]: any;
  },
  reportedUrl?: string
) => ({
  onError: reportedUrl
    ? (error: any) => trackFetchError(error, reportedUrl)
    : trackFetchError,
  ...swrOptions,
});
